import { useContext, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronRight } from '@/assets/icons'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { getAnalyticsApiErrorProps } from '@/analytics/utils/analytics-helpers'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { AuthContext } from '@/context/auth-context'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { MatterSettingsConnectedAccountCard } from '@/matter/components/matter-settings/MatterSettingsConnectedAccountCard'
import { selectMatterById, selectMatterMembers } from '@/matter/store/selectors'
import { canEditMatter } from '@/matter/utils/matter-permissions'
import { matterPaths } from '@/routing/paths'
import { pickVisibleAccount } from '@/settings/utils/pick-visible-account'
import type { ExternalStorageDisconnectFileDisposition } from '@/settings/schemas/external-storage-schema'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { SelectExternalFolderModal } from '@/user_files/external-storage/components/SelectExternalFolderModal'
import { DisconnectMatterFolderDialog } from '@/user_files/external-storage/components/DisconnectMatterFolderDialog'
import { deleteMatterExternalSyncMapping } from '@/user_files/external-storage/fetch/delete-matter-external-sync-mapping'
import { isMatterSyncConnector } from '@/user_files/external-storage/utils/is-matter-sync-connector'
import {
  ExternalStorageActions,
  kExternalStorageRequestStatus,
  selectConnectedAccounts,
  selectExternalStorageAccountsRequest,
  selectMatterExternalSyncMapping,
  selectMatterExternalSyncMappingRequest,
} from '@/user_files/external-storage/store/slice'
import { selectCurrentFolder, selectDriveListSortForMatter } from '@/user_files/folders/store/selectors'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { UserFilesActions } from '@/user_files/files/store/slice'

export default function MatterSettingsPage() {
  const { matterId } = useParams<{ matterId: string }>()
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const { user, userAccountData } = useContext(AuthContext)
  const organizationId = getDriveSyncOrganizationId(userAccountData)
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))
  const members = useAppSelector((state) => selectMatterMembers(state, matterId))
  const accounts = useAppSelector(selectConnectedAccounts)
  const accountsRequest = useAppSelector(selectExternalStorageAccountsRequest)
  const syncMapping = useAppSelector((state) => selectMatterExternalSyncMapping(state, matterId))
  const mappingRequest = useAppSelector((state) => selectMatterExternalSyncMappingRequest(state, matterId))
  const currentFolder = useAppSelector(selectCurrentFolder)
  const driveListSort = useAppSelector((state) => selectDriveListSortForMatter(state, { matterId: matterId ?? '' }))
  const [selectFolderOpen, setSelectFolderOpen] = useState(false)
  const [disconnectFolderDialogOpen, setDisconnectFolderDialogOpen] = useState(false)
  const [disconnectingFolder, setDisconnectingFolder] = useState(false)

  useEffect(() => {
    dispatch(ExternalStorageActions.fetchConnectedAccounts())
    if (matterId) {
      dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping({ matterId, force: true }))
    }
  }, [dispatch, matterId])

  const handleFolderImportSuccess = () => {
    if (!matterId) return
    // The picker owns the forced post-write refresh. This ordinary request is a caller-level
    // fallback and is deduplicated while that authoritative request is in flight.
    dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping({ matterId }))
  }

  const handleFolderDisconnect = async (fileDisposition: ExternalStorageDisconnectFileDisposition) => {
    if (!matterId || !activeSyncMapping || !canDisconnectFolder) return

    setDisconnectingFolder(true)
    try {
      await deleteMatterExternalSyncMapping(matterId, fileDisposition)
      setDisconnectFolderDialogOpen(false)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: 'Synced folder disconnected.',
          durationMs: kToastDurationShortMs,
        })
      )
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncFolderDisconnected,
        { matterId, organizationId, externalStorageProvider: activeSyncMapping.provider },
        trackEvent
      )
    } catch (error) {
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Failed to disconnect synced folder. Please try again.',
          durationMs: kToastDurationShortMs,
        })
      )
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncFolderDisconnectFailed,
        { matterId, organizationId, externalStorageProvider: activeSyncMapping.provider, ...getAnalyticsApiErrorProps(error) },
        trackEvent
      )
    } finally {
      dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping({ matterId, force: true }))
      if (currentFolder?.matter_id === matterId) {
        dispatch(UserFoldersActions.fetchFolderChildren({ folderId: currentFolder.id, sort: driveListSort }))
        dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolder.id, sort: driveListSort }))
      }
      setDisconnectingFolder(false)
    }
  }

  const ownVisibleAccount = pickVisibleAccount(accounts)
  const activeSyncMapping = syncMapping?.status === 'active' ? syncMapping : null
  const isConnector = isMatterSyncConnector(activeSyncMapping, user?.uid)
  const isMatterOwner = canEditMatter(matter)
  const folderPickerAccount = activeSyncMapping ? accounts.find((account) => account.id === activeSyncMapping.account_id) ?? null : ownVisibleAccount

  const canChooseFolder = folderPickerAccount?.status === 'connected' && (activeSyncMapping == null || isConnector)
  const canDisconnectFolder = activeSyncMapping != null && (isConnector || isMatterOwner)

  const accountDisplay = activeSyncMapping
    ? {
        provider: activeSyncMapping.provider,
        displayEmail: activeSyncMapping.account_display_email,
        status: activeSyncMapping.account_status,
      }
    : ownVisibleAccount
    ? { provider: ownVisibleAccount.provider, displayEmail: ownVisibleAccount.display_email, status: ownVisibleAccount.status }
    : null
  const connectorMember = activeSyncMapping ? members.find((member) => member.user_id === activeSyncMapping.connected_by_user_id) : null
  const connectorLabel = connectorMember?.display_name?.trim() || connectorMember?.email?.trim() || null
  const syncedFolderName = activeSyncMapping?.external_folder_name ?? activeSyncMapping?.external_folder_path ?? activeSyncMapping?.external_folder_id ?? null
  const syncFailureMessage = activeSyncMapping?.failure_message ?? null

  const matterOverviewPath = matterId ? matterPaths.overview(matterId) : '/dashboard/matters'
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)

  const loading =
    (activeSyncMapping == null &&
      accounts.length === 0 &&
      (accountsRequest.status === kExternalStorageRequestStatus.IDLE || accountsRequest.status === kExternalStorageRequestStatus.LOADING)) ||
    (!!matterId &&
      syncMapping == null &&
      (mappingRequest.status === kExternalStorageRequestStatus.IDLE || mappingRequest.status === kExternalStorageRequestStatus.LOADING))
  const error =
    (activeSyncMapping == null && accountsRequest.status === kExternalStorageRequestStatus.ERROR) ||
    mappingRequest.status === kExternalStorageRequestStatus.ERROR
      ? 'Failed to load connected accounts or sync settings.'
      : null

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex h-[60px] shrink-0 items-center gap-1 border-b border-brand-neutral-200 px-3 lg:ml-4 lg:pl-2">
        <Link to={matterOverviewPath} className="min-w-0 truncate rounded-md px-2 py-1 text-sm font-semibold text-brand-neutral-600 hover:bg-brand-neutral-100">
          {matter?.name ?? 'Matter'}
        </Link>
        <ChevronRight className="h-5 w-5 shrink-0 text-brand-neutral-600" />
        <span className="shrink-0 rounded-md px-2 py-1 text-sm font-semibold text-brand-neutral-950">Matter Settings</span>
      </div>

      <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto px-4 py-6 lg:px-10">
        <MatterSettingsConnectedAccountCard
          driveSyncEnabled={driveSyncEnabled}
          error={error}
          loading={loading}
          canChooseFolder={canChooseFolder}
          canDisconnectFolder={canDisconnectFolder}
          isConnector={isConnector}
          connectorLabel={connectorLabel}
          disconnectingFolder={disconnectingFolder}
          syncedFolderName={syncedFolderName}
          lastSyncedAt={activeSyncMapping?.last_synced_at ?? null}
          syncFailureMessage={syncFailureMessage}
          accountDisplay={accountDisplay}
          onChooseFolder={() => setSelectFolderOpen(true)}
          onDisconnectFolder={() => setDisconnectFolderDialogOpen(true)}
        />
      </div>

      {matterId && folderPickerAccount && canChooseFolder && (
        <SelectExternalFolderModal
          account={folderPickerAccount}
          currentExternalFolderId={syncMapping?.external_folder_id}
          matterId={matterId}
          open={selectFolderOpen}
          onOpenChange={setSelectFolderOpen}
          replaceExistingMapping={syncMapping != null}
          matterDriveSyncEntrySource={MatterDriveSyncEntrySource.MatterSettings}
          onSuccess={handleFolderImportSuccess}
        />
      )}

      {matterId && activeSyncMapping && canDisconnectFolder && syncedFolderName && (
        <DisconnectMatterFolderDialog
          open={disconnectFolderDialogOpen}
          matterId={matterId}
          folderName={syncedFolderName}
          provider={activeSyncMapping.provider}
          loading={disconnectingFolder}
          onOpenChange={setDisconnectFolderDialogOpen}
          onConfirm={handleFolderDisconnect}
        />
      )}
    </div>
  )
}
